import { DURATION_UNITS, DURATION_UNIT_SECONDS, type DurationUnit } from '@aysar/contracts';

export interface DurationBounds {
  /** In base units. */
  min?: number;
  max?: number;
}

/** How many `to` units one `from` unit is. Always exact — see `DURATION_UNITS`. */
function ratio(from: DurationUnit, to: DurationUnit): number {
  return DURATION_UNIT_SECONDS[from] / DURATION_UNIT_SECONDS[to];
}

/** An amount written in `from`, expressed in the unit the value is stored in. */
export function toBase(amount: number, from: DurationUnit, base: DurationUnit): number {
  return amount * ratio(from, base);
}

/** A stored amount, expressed in the unit the operator is looking at. */
export function toDisplay(stored: number, base: DurationUnit, display: DurationUnit): number {
  return stored * ratio(base, display);
}

/**
 * The units this setting may be edited in.
 *
 * A unit is offered when **both** of its bounds are a whole number of it, and
 * when at least one whole unit fits inside the range. That single rule buys the
 * invariant everything else depends on: **the number in the box is always an
 * integer**, so an amount can never round on its way to storage.
 *
 * It also produces the right answers on the settings that exist without anyone
 * hand-listing them: `media.readUrlTtlSeconds` (30–900s) offers seconds only,
 * because 30 is not a whole number of minutes and offering minutes would make
 * the bottom of its own legal range untypeable. `auth.accessTokenTtlSeconds`
 * (60–3600s) offers seconds and minutes but not hours, because 60s is not a
 * whole hour.
 *
 * The base unit is always offered — a setting whose bounds divide badly must
 * still be editable.
 */
export function offeredUnits(base: DurationUnit, bounds: DurationBounds): readonly DurationUnit[] {
  const baseIndex = DURATION_UNITS.indexOf(base);

  return DURATION_UNITS.filter((unit, index) => {
    if (index < baseIndex) return false;
    if (unit === base) return true;

    const size = ratio(unit, base);
    const { min, max } = bounds;

    if (min !== undefined && min % size !== 0) return false;
    if (max !== undefined && max % size !== 0) return false;
    // One whole unit has to fit, or every legal value is a fraction of it.
    if (max !== undefined && max < size) return false;

    return true;
  });
}

/**
 * The unit to show a stored value in: the **largest offered unit it divides
 * exactly**, so 900 seconds reads "15 minutes" and 3601 seconds reads "3601
 * seconds" rather than "1.0003 hours".
 *
 * Zero is special-cased to the base unit. It divides every unit exactly, so the
 * general rule would render "0 days" for a value measured in seconds — true,
 * and no help at all in telling the operator what they are editing.
 */
export function bestUnitFor(
  stored: number | null | undefined,
  base: DurationUnit,
  offered: readonly DurationUnit[],
): DurationUnit {
  if (stored === null || stored === undefined || stored === 0) return base;

  const exact = [...offered]
    .filter((unit) => stored % ratio(unit, base) === 0)
    .sort((a, b) => DURATION_UNIT_SECONDS[b] - DURATION_UNIT_SECONDS[a]);

  return exact[0] ?? base;
}
